import { useCallback, useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'

import { getSettings, listBoards, patchSettings, type BoardListItem } from '../api/client'

/**
 * 看板页左侧的可折叠看板列表（只列看板，不含 Home / My work 等）。
 *
 * 设计要点：
 *   · **浮层实现**（`position: fixed`），展开/收起不改变右侧看板的布局宽度。
 *     这是刻意的：分组拖动依赖每个分组的实时矩形，任何横向重排都可能让拖动变脆。
 *   · 展开状态只存内存（刷新后回到收起态），不写 localStorage —— 项目规矩是不信任前端存储。
 *   · 看板名超长自动换行（`white-space: normal` + `word-break`），不截断成省略号。
 */
export default function BoardDrawer() {
  const [open, setOpen] = useState(false)
  /** 首屏是否已经从服务端恢复过偏好（只读一次） */
  /** 落库失败时的提示（显示在面板里的小字，不弹窗） */
  const [saveError, setSaveError] = useState('')
  /** 切换开关：乐观更新，失败回滚 */
  const toggle = useCallback(() => {
    const next = !open
    setOpen(next)
    setSaveError('')
    void (async () => {
      try {
        await patchSettings({ 'ui.boardDrawerOpen': next ? '1' : '0' })
      } catch (e) {
        setOpen(!next)          // 回滚
        setSaveError(e instanceof Error ? e.message : '状态保存失败')
      }
    })()
  }, [open])
  const [boards, setBoards] = useState<BoardListItem[] | null>(null)
  const [error, setError] = useState('')
  const { id } = useParams()
  const activeId = id === undefined ? null : Number(id)

  const load = useCallback(async () => {
    try {
      const res = await listBoards()
      setBoards(res.boards)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  // 首屏读一次界面偏好（不阻塞渲染：失败就保持收起）
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const res = await getSettings()
        if (alive && res.prefs?.boardDrawerOpen === true) setOpen(true)
      } catch {
        /* 读不到偏好就用默认收起态，不影响页面 */
      } finally {
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  // 首次展开时再拉列表，避免进任何页面都多一次请求
  useEffect(() => {
    if (open && boards === null) void load()
  }, [open, boards, load])

  // 切换看板后回到收起态，避免遮挡内容
  useEffect(() => {
    setOpen(false)
  }, [activeId])

  return (
    <div className={`drawer${open ? ' drawer--open' : ''}`}>
      <button
        type="button"
        className="drawer__toggle"
        aria-label={open ? '收起看板列表' : '展开看板列表'}
        aria-expanded={open}
        title={open ? '收起看板列表' : '展开看板列表'}
        onClick={toggle}
      >
        {open ? '‹' : '›'}
      </button>

      {open && (
        <nav className="drawer__panel" aria-label="看板列表">
          <div className="drawer__title">看板</div>
          {saveError !== '' && <p className="drawer__err">{saveError}</p>}
          {error !== '' && <p className="drawer__err">{error}</p>}
          {boards === null && error === '' && <p className="drawer__dim">加载中…</p>}
          {boards !== null && boards.length === 0 && <p className="drawer__dim">还没有看板</p>}
          <ul className="drawer__list">
            {(boards ?? []).map((b) => (
              <li key={b.id}>
                <NavLink
                  to={`/boards/${b.id}`}
                  className={({ isActive }) => `drawer__item${isActive ? ' drawer__item--active' : ''}`}
                  title={b.name}
                >
                  {/* 小看板图标：圆角矩形 + 顶部标题条（颜色跟随文字） */}
                  <svg
                    className="drawer__icon"
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <rect x="0.6" y="0.6" width="12.8" height="10.8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
                    <line x1="0.6" y1="4" x2="13.4" y2="4" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                  <span className="drawer__name">{b.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  )
}
